import { SettingOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Modal,
  Switch,
  Tag,
  Typography,
  message
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"

import { CustomTimePicker } from "../../../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { TimezoneSelect } from "../../../../../components/Shared/TimezoneSelect.tsx"
import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import {
  fetchNotificationSettings,
  updateNotificationSettings
} from "../../../../Notifications/redux/notifications.thunks.ts"
import { SmsAlertsNotice } from "../../../../Notifications/sms/SmsAlertsNotice.tsx"
import { useSmsVerification } from "../../../../Notifications/sms/use-sms-verification.ts"
import type { CalendarNotificationPreferences } from "../../../helpers/calendar-notification-preferences.ts"
import { useCalendarNotificationPreferences } from "../../../helpers/use-calendar-notification-preferences.ts"
import { useWorkingSchedule } from "../../../helpers/use-working-schedule.ts"
import {
  ALL_WEEKDAYS,
  DEFAULT_WORKING_SCHEDULE,
  type DaySchedule,
  WEEKDAY_LABELS,
  type WeekdayIndex,
  type WorkingSchedule,
  areEnabledDaysUniform,
  validateWorkingSchedule
} from "../../../helpers/working-schedule.ts"
import { CalendarAddReminderOffsetField } from "../CalendarAddForms/CalendarAddReminderOffsetField.tsx"
import {
  BASE_CHANNELS,
  type CalendarNotificationChannel,
  type CalendarNotificationTypeDraft,
  CalendarNotificationTypesTable,
  offersSms as offersSmsFor,
  optionalChannelsFor
} from "./CalendarNotificationTypesTable.tsx"

const { Title, Text } = Typography

const parseTime = (time: string): Dayjs => {
  const [hour, minute] = time.split(":").map(Number)
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0)
}

const formatTime = (value: Dayjs): string => value.format("HH:mm")

// Display/Event Defaults are not implemented. They're named here so the nav
// reads as a real settings surface with room to grow rather than a form with
// a decorative sidebar bolted on — each renders disabled with a "Soon" tag,
// never a working control with nothing behind it.
const SETTINGS_SECTIONS = [
  { key: "working-schedule", label: "Working Schedule", available: true },
  { key: "display", label: "Display", available: false },
  { key: "event-defaults", label: "Event Defaults", available: false },
  { key: "notifications", label: "Notifications", available: true }
] as const

type SettingsSectionKey = (typeof SETTINGS_SECTIONS)[number]["key"]

interface CalendarSettingsDrawerProps {
  open: boolean
  accountTimezone?: string | null
  onClose: () => void
}

function SmsAwareNotificationTypes(
  props: React.ComponentProps<typeof CalendarNotificationTypesTable>
) {
  const dispatch = useAppDispatch()
  const sms = useSmsVerification()
  const offersSms = !!props.items?.some(offersSmsFor)

  return (
    <Flex vertical gap={8}>
      {offersSms && (
        <SmsAlertsNotice
          state={sms.state}
          lock={sms.lock}
          failed={sms.failed}
          onRetry={() => void sms.reload()}
          onVerified={(verified) => {
            sms.setState(verified)
            void dispatch(fetchNotificationSettings())
          }}
        />
      )}
      <CalendarNotificationTypesTable {...props} smsLock={sms.lock} />
    </Flex>
  )
}

export function CalendarSettingsDrawer({
  open,
  accountTimezone,
  onClose
}: CalendarSettingsDrawerProps) {
  const [activeSection, setActiveSection] =
    useState<SettingsSectionKey>("working-schedule")

  const [schedule, setSchedule] = useWorkingSchedule()
  const [draft, setDraft] = useState<WorkingSchedule>(schedule)
  const [sameHoursForAllDays, setSameHoursForAllDays] = useState(() =>
    areEnabledDaysUniform(schedule)
  )

  const [notificationPreferences, setNotificationPreferences] =
    useCalendarNotificationPreferences()
  const [notificationDraft, setNotificationDraft] =
    useState<CalendarNotificationPreferences>(notificationPreferences)

  // The app-wide `CALENDAR` notification-type group (NotificationSettingsForm's
  // own data source) — reused here rather than duplicated, so this table and
  // the global Settings > Notifications page can never disagree about what a
  // user has configured.
  const dispatch = useAppDispatch()
  const notificationSettings = useAppSelector(
    (state) => state.notification.notificationSettings
  )
  const fetchingNotificationSettings = useAppSelector(
    (state) => state.notification.fetchingSettings
  )
  const notificationSettingsError = useAppSelector(
    (state) => state.notification.settingsError
  )
  const calendarNotificationTypes = notificationSettings?.CALENDAR ?? null
  const [notificationTypesDraft, setNotificationTypesDraft] = useState<
    Record<number, CalendarNotificationTypeDraft>
  >({})

  // Reopening always starts from the last saved value — an unsaved edit left
  // over from a previous open must never leak into the next one.
  useEffect(() => {
    if (!open) return
    setActiveSection("working-schedule")
    setDraft(schedule)
    setSameHoursForAllDays(areEnabledDaysUniform(schedule))
    setNotificationDraft(notificationPreferences)
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- re-seeding when a saved value changes would overwrite in-progress edits if a preference load resolves while the drawer is open
  }, [open])

  // Loaded lazily — only once the user actually opens the Notifications
  // section — rather than on every drawer open, since most visits are for
  // Working Schedule alone. `calendarNotificationTypes` only changes on a
  // fetch or a save this same drawer triggers, so re-seeding the draft here
  // never clobbers an in-progress edit with unrelated app state.
  useEffect(() => {
    if (!open || activeSection !== "notifications") return
    // `notificationSettingsError` has to be in this guard, not just checked
    // once: `fetchingNotificationSettings` flips back to false on a failed
    // fetch too, which would otherwise re-satisfy this effect's own deps and
    // immediately re-fire it — turning the table's explicit Retry button
    // into a silent, uncontrolled auto-retry loop.
    if (
      notificationSettings ||
      fetchingNotificationSettings ||
      notificationSettingsError
    )
      return
    void dispatch(fetchNotificationSettings())
  }, [
    open,
    activeSection,
    notificationSettings,
    fetchingNotificationSettings,
    notificationSettingsError,
    dispatch
  ])

  useEffect(() => {
    if (!open || !calendarNotificationTypes) return
    setNotificationTypesDraft(
      Object.fromEntries(
        calendarNotificationTypes.map((item) => [
          item.notification_type,
          {
            email: item.email,
            in_app: item.in_app,
            phone: item.phone,
            desktop: item.desktop,
            ...Object.fromEntries(
              optionalChannelsFor(item).map((channel) => [
                channel,
                item[channel]
              ])
            )
          }
        ])
      )
    )
  }, [open, calendarNotificationTypes])

  const validation = useMemo(() => validateWorkingSchedule(draft), [draft])
  const scheduleChanged = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(schedule),
    [draft, schedule]
  )
  const notificationsChanged = useMemo(
    () =>
      JSON.stringify(notificationDraft) !==
      JSON.stringify(notificationPreferences),
    [notificationDraft, notificationPreferences]
  )
  const notificationTypesChanged = useMemo(() => {
    if (!calendarNotificationTypes) return false
    return calendarNotificationTypes.some((item) => {
      const row = notificationTypesDraft[item.notification_type]
      if (!row) return false
      return [...BASE_CHANNELS, ...optionalChannelsFor(item)].some(
        (channel) => row[channel] !== item[channel]
      )
    })
  }, [calendarNotificationTypes, notificationTypesDraft])
  const hasUnsavedChanges =
    scheduleChanged || notificationsChanged || notificationTypesChanged
  // An invalid schedule only has to block Save when it's actually about to
  // be written — a pre-existing invalid schedule the user hasn't touched
  // this session (scheduleChanged false) must never block an unrelated
  // Notifications-only save, since handleSave never writes it in that case.
  const canSave = validation.isValid || !scheduleChanged

  const handleNotificationTypeChange = (
    notificationType: number,
    channel: CalendarNotificationChannel,
    value: boolean
  ) => {
    setNotificationTypesDraft((prev) => {
      const base =
        prev[notificationType] ??
        calendarNotificationTypes?.find(
          (i) => i.notification_type === notificationType
        )
      if (!base) return prev
      return { ...prev, [notificationType]: { ...base, [channel]: value } }
    })
  }

  const updateDay = (day: WeekdayIndex, patch: Partial<DaySchedule>) => {
    setDraft((prev) => ({
      ...prev,
      days: { ...prev.days, [day]: { ...prev.days[day], ...patch } }
    }))
  }

  const applyHoursToAllDays = (start: string, end: string) => {
    setDraft((prev) => ({
      ...prev,
      days: ALL_WEEKDAYS.reduce(
        (acc, day) => {
          acc[day] = { ...prev.days[day], start, end }
          return acc
        },
        {} as WorkingSchedule["days"]
      )
    }))
  }

  const firstEnabledDay = ALL_WEEKDAYS.find((day) => draft.days[day].enabled)
  const uniformDay = draft.days[firstEnabledDay ?? 1]

  const handleSameHoursToggle = (checked: boolean) => {
    setSameHoursForAllDays(checked)
    if (!checked) return
    const source = firstEnabledDay
      ? draft.days[firstEnabledDay]
      : DEFAULT_WORKING_SCHEDULE.days[1]
    applyHoursToAllDays(source.start, source.end)
  }

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose()
      return
    }
    setConfirmDiscardOpen(true)
  }

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false)
    onClose()
  }

  const handleSave = () => {
    if (!canSave) return
    // Only write the section(s) that actually changed — each persists under
    // its own key independently (schedule/reminder via useSurfacePreference,
    // notification types via the shared NotificationPerUser endpoint).
    if (scheduleChanged) setSchedule(draft)
    if (notificationsChanged) setNotificationPreferences(notificationDraft)
    if (notificationTypesChanged && calendarNotificationTypes) {
      void dispatch(
        updateNotificationSettings(
          calendarNotificationTypes.map((item) => ({
            ...item,
            ...(notificationTypesDraft[item.notification_type] ?? {})
          }))
        )
      )
    }
    // updateNotificationSettings already shows its own confirmation toast —
    // only add this one when something outside that endpoint changed, so a
    // notification-types-only save doesn't show two.
    if (scheduleChanged || notificationsChanged) {
      message.success("Calendar settings updated")
    }
    onClose()
  }

  const handleTimezoneChange = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      // Picking the account's own timezone clears the override rather than
      // pinning that value — so a later account-timezone change (made from
      // Settings > Profile) is still followed live instead of going stale.
      timezone: value === accountTimezone ? null : value
    }))
  }

  const dayErrorMessage = useMemo(() => {
    const entries = Object.entries(validation.dayErrors) as [string, string][]
    if (entries.length === 0) return null
    if (sameHoursForAllDays) return "Start time must be before end time."
    return entries
      .map(
        ([day, msg]) => `${WEEKDAY_LABELS[Number(day) as WeekdayIndex]}: ${msg}`
      )
      .join(" ")
  }, [validation.dayErrors, sameHoursForAllDays])

  return (
    <Drawer
      open={open}
      width={560}
      title={
        <Flex align="center" gap={8}>
          <SettingOutlined />
          <span>Calendar Settings</span>
        </Flex>
      }
      onClose={handleClose}
      destroyOnClose
      footer={
        <Flex justify="end" gap="small">
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" disabled={!canSave} onClick={handleSave}>
            Save
          </Button>
        </Flex>
      }
    >
      <Flex gap={20} align="start">
        <nav
          aria-label="Calendar settings sections"
          className="flex w-[124px] shrink-0 flex-col gap-0.5"
        >
          {SETTINGS_SECTIONS.map((section) =>
            section.available ? (
              <button
                key={section.key}
                type="button"
                aria-current={activeSection === section.key}
                onClick={() => setActiveSection(section.key)}
                className={`rounded-md px-3 py-2 text-left text-sm font-medium ${
                  activeSection === section.key
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {section.label}
              </button>
            ) : (
              <button
                key={section.key}
                type="button"
                disabled
                className="flex cursor-not-allowed items-center justify-between rounded-md px-3 py-2 text-left text-sm text-gray-400"
              >
                {section.label}
                <Tag className="ml-1 text-[10px] leading-4">Soon</Tag>
              </button>
            )
          )}
        </nav>

        {activeSection === "working-schedule" ? (
          <Flex vertical gap={20} className="min-w-0 flex-1">
            <Flex vertical gap={4}>
              <Title level={5} className="mb-0">
                Working Schedule
              </Title>
              <Text type="secondary" className="text-sm">
                Set your working days, hours and time zone — Calendar uses this
                to show what&apos;s available.
              </Text>
            </Flex>

            <Flex vertical gap={8}>
              <Text strong>Work Week</Text>
              <Flex wrap gap={12}>
                {ALL_WEEKDAYS.map((day) => (
                  <Checkbox
                    key={day}
                    checked={draft.days[day].enabled}
                    onChange={(e) =>
                      updateDay(day, { enabled: e.target.checked })
                    }
                  >
                    {WEEKDAY_LABELS[day]}
                  </Checkbox>
                ))}
              </Flex>
            </Flex>

            <Flex vertical gap={8}>
              <Flex justify="space-between" align="center">
                <Text strong>Working Hours</Text>
                <Flex gap={8} align="center">
                  <Text type="secondary" className="text-xs">
                    Different hours per day
                  </Text>
                  <Switch
                    size="small"
                    aria-label="Use different working hours per day"
                    checked={!sameHoursForAllDays}
                    onChange={(checked) => handleSameHoursToggle(!checked)}
                  />
                </Flex>
              </Flex>

              {sameHoursForAllDays ? (
                <Flex align="center" gap={8}>
                  <CustomTimePicker
                    aria-label="Start time"
                    value={parseTime(uniformDay.start)}
                    onChange={(value) =>
                      value &&
                      applyHoursToAllDays(formatTime(value), uniformDay.end)
                    }
                  />
                  <Text type="secondary">—</Text>
                  <CustomTimePicker
                    aria-label="End time"
                    value={parseTime(uniformDay.end)}
                    onChange={(value) =>
                      value &&
                      applyHoursToAllDays(uniformDay.start, formatTime(value))
                    }
                  />
                </Flex>
              ) : (
                <Flex vertical gap={6}>
                  {ALL_WEEKDAYS.map((day) => {
                    const daySchedule = draft.days[day]
                    return (
                      <Flex key={day} align="center" gap={8}>
                        <Text
                          className={`w-24 shrink-0 ${daySchedule.enabled ? "" : "text-gray-400"}`}
                        >
                          {WEEKDAY_LABELS[day]}
                        </Text>
                        <CustomTimePicker
                          aria-label={`${WEEKDAY_LABELS[day]} start time`}
                          disabled={!daySchedule.enabled}
                          value={parseTime(daySchedule.start)}
                          onChange={(value) =>
                            value &&
                            updateDay(day, { start: formatTime(value) })
                          }
                        />
                        <Text type="secondary">—</Text>
                        <CustomTimePicker
                          aria-label={`${WEEKDAY_LABELS[day]} end time`}
                          disabled={!daySchedule.enabled}
                          value={parseTime(daySchedule.end)}
                          onChange={(value) =>
                            value && updateDay(day, { end: formatTime(value) })
                          }
                        />
                      </Flex>
                    )
                  })}
                </Flex>
              )}
              {dayErrorMessage && (
                <Alert type="error" showIcon message={dayErrorMessage} />
              )}
            </Flex>

            <Flex vertical gap={8}>
              <Flex justify="space-between" align="center">
                <Text strong>Break</Text>
                <Switch
                  size="small"
                  aria-label="Enable break"
                  checked={draft.breakTime.enabled}
                  onChange={(checked) =>
                    setDraft((prev) => ({
                      ...prev,
                      breakTime: { ...prev.breakTime, enabled: checked }
                    }))
                  }
                />
              </Flex>
              {draft.breakTime.enabled && (
                <Flex align="center" gap={8}>
                  <CustomTimePicker
                    aria-label="Break start time"
                    value={parseTime(draft.breakTime.start)}
                    onChange={(value) =>
                      value &&
                      setDraft((prev) => ({
                        ...prev,
                        breakTime: {
                          ...prev.breakTime,
                          start: formatTime(value)
                        }
                      }))
                    }
                  />
                  <Text type="secondary">—</Text>
                  <CustomTimePicker
                    aria-label="Break end time"
                    value={parseTime(draft.breakTime.end)}
                    onChange={(value) =>
                      value &&
                      setDraft((prev) => ({
                        ...prev,
                        breakTime: { ...prev.breakTime, end: formatTime(value) }
                      }))
                    }
                  />
                </Flex>
              )}
              {validation.breakError && (
                <Alert type="error" showIcon message={validation.breakError} />
              )}
            </Flex>

            <Flex vertical gap={8}>
              <Text strong>Time Zone</Text>
              <TimezoneSelect
                aria-label="Calendar time zone"
                value={draft.timezone ?? accountTimezone ?? undefined}
                onChange={handleTimezoneChange}
              />
            </Flex>
          </Flex>
        ) : (
          <Flex vertical gap={20} className="min-w-0 flex-1">
            <Flex vertical gap={4}>
              <Title level={5} className="mb-0">
                Notifications
              </Title>
              <Text type="secondary" className="text-sm">
                Control the default reminder for new events, and manage which
                Calendar notifications you receive.
              </Text>
            </Flex>

            <Flex vertical gap={8}>
              <Text strong>Default reminder</Text>
              <CalendarAddReminderOffsetField
                value={notificationDraft.defaultReminderOffset}
                onChange={(value) =>
                  setNotificationDraft((prev) => ({
                    ...prev,
                    defaultReminderOffset: value
                  }))
                }
                className="flex w-fit items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm"
                // This picks a future default, not "remind me about this real
                // thing now" — the desktop-permission ask belongs to the
                // Desktop channel toggle below, not this value.
                promptForDesktop={false}
              />
              <Text type="secondary" className="text-xs">
                New events start with this reminder — you can still change it
                per event.
              </Text>
            </Flex>

            <Flex vertical gap={8}>
              <Flex vertical gap={0}>
                <Text strong>Calendar notifications</Text>
                <Text type="secondary" className="text-xs">
                  Choose how you&apos;re notified about Calendar events —
                  in-app, push, or email.
                </Text>
              </Flex>
              <SmsAwareNotificationTypes
                items={calendarNotificationTypes}
                draft={notificationTypesDraft}
                onChange={handleNotificationTypeChange}
                loading={
                  fetchingNotificationSettings && !calendarNotificationTypes
                }
                error={notificationSettingsError && !calendarNotificationTypes}
                onRetry={() => dispatch(fetchNotificationSettings())}
              />
            </Flex>
          </Flex>
        )}
      </Flex>

      <Modal
        open={confirmDiscardOpen}
        title="Discard unsaved changes?"
        okText="Discard"
        okButtonProps={{ danger: true }}
        cancelText="Keep editing"
        onOk={handleConfirmDiscard}
        onCancel={() => setConfirmDiscardOpen(false)}
      >
        Your calendar settings changes have not been saved.
      </Modal>
    </Drawer>
  )
}
